import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import Icon from '../common/Icon';
import './Skills.css';

interface SkillData {
  name: string;
  percentage: number;
  icon: string;
  color: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
  category: 'AI & Agentic Systems' | 'Language' | 'Framework' | 'Backend & Databases' | 'DevOps & Tools' | 'Soft Skill';
  description: string;
}

interface SkillsProps {
  limit?: number;
}

const Skills: React.FC<SkillsProps> = ({ limit }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const skillsHeaderRef = React.useRef(null);
  const skillsHeaderInView = useInView(skillsHeaderRef, { once: true, amount: 0.3 });

  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [showAll, setShowAll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const skills: SkillData[] = [
    // --- AI & AGENTIC SYSTEMS ---
    { 
        name: 'LangChain & LangGraph', 
        percentage: 95, 
        icon: 'FaNetworkWired', 
        color: '#10B981',
        level: 'Expert',
        category: 'AI & Agentic Systems',
        description: 'Orchestrating autonomous agents, multi-turn state machines, and LLM logical graphs for complex tasks.'
    },
    { 
        name: 'RAG-Based Frameworks', 
        percentage: 90, 
        icon: 'FaBrain', 
        color: '#EC4899',
        level: 'Expert',
        category: 'AI & Agentic Systems',
        description: 'Building custom Retrieval-Augmented Generation engines, chunking pipelines, and vector semantic searches.'
    },
    { 
        name: 'Model Fine-Tuning (PEFT/QLoRA)', 
        percentage: 80, 
        icon: 'FaCogs', 
        color: '#EF4444',
        level: 'Advanced',
        category: 'AI & Agentic Systems',
        description: 'Parameter-Efficient Fine-Tuning and low-rank adaptation on open-weights LLMs for specialized downstream domain tasks.'
    },
    { 
        name: 'Agent Orchestration & Memory', 
        percentage: 95, 
        icon: 'FaRobot', 
        color: '#6366F1',
        level: 'Expert',
        category: 'AI & Agentic Systems',
        description: 'Implementing custom agent tool environments, MCP integration workflows, and episodic short/long-term memory structures.'
    },
    { 
        name: 'Prompt Engineering', 
        percentage: 95, 
        icon: 'FaKeyboard', 
        color: '#F59E0B',
        level: 'Expert',
        category: 'AI & Agentic Systems',
        description: 'Designing system prompts, multi-turn conversation templates, few-shot schemas, and self-correction reasoning logs.'
    },

    // --- LANGUAGES ---
    { 
        name: 'C++', 
        percentage: 95, 
        icon: 'FaCode', 
        color: '#00599C',
        level: 'Expert',
        category: 'Language',
        description: 'Proficient in object-oriented C++ with rigorous experience in pointer mathematics, custom data structures, and algorithms.'
    },
    { 
        name: 'Python', 
        percentage: 90, 
        icon: 'FaPython', 
        color: '#306998',
        level: 'Expert',
        category: 'Language',
        description: 'Expertise in asynchronous python programming, automation tasks, NLP workflows, and backend API orchestration.'
    },
    { 
        name: 'JavaScript / TypeScript', 
        percentage: 85, 
        icon: 'SiJavascript', 
        color: '#F7DF1E',
        level: 'Advanced',
        category: 'Language',
        description: 'Skilled in dynamic web scripting, TypeScript static typing layouts, asynchronous actions, and state handling.'
    },
    { 
        name: 'SQL', 
        percentage: 90, 
        icon: 'SiMysql', 
        color: '#4479A1',
        level: 'Expert',
        category: 'Language',
        description: 'Writing complex relational queries, executing table normalizations, indexing, and structuring query optimizations.'
    },
    { 
        name: 'Assembly (Intel 8088)', 
        percentage: 70, 
        icon: 'FaMicrochip', 
        color: '#7C3AED',
        level: 'Intermediate',
        category: 'Language',
        description: 'Creating low-level drivers, VGA video buffers writes, keyboard hooks, and retro games using BIOS interrupts.'
    },

    // --- FRAMEWORKS ---
    { 
        name: 'React.js', 
        percentage: 85, 
        icon: 'FaReact', 
        color: '#61DAFB',
        level: 'Advanced',
        category: 'Framework',
        description: 'Crafting responsive modular components, handling state graphs (Hooks, Context), and visual layout structures.'
    },
    { 
        name: 'FastAPI', 
        percentage: 90, 
        icon: 'SiFastapi', 
        color: '#009688',
        level: 'Expert',
        category: 'Framework',
        description: 'Building blazing-fast asynchronous APIs with full Pydantic validation, JWT security, and SQLAlchemy ORM connections.'
    },
    { 
        name: 'Django', 
        percentage: 80, 
        icon: 'SiDjango', 
        color: '#092E20',
        level: 'Advanced',
        category: 'Framework',
        description: 'Structuring full-stack web applications, administering relational models, database migrations, and templates.'
    },
    { 
        name: 'Tailwind CSS', 
        percentage: 90, 
        icon: 'SiTailwindcss', 
        color: '#06B6D4',
        level: 'Expert',
        category: 'Framework',
        description: 'Developing highly tailored custom utility-first web interfaces with fully responsive device breakpoints.'
    },

    // --- BACKEND & DATABASES ---
    { 
        name: 'SQLAlchemy & ORM', 
        percentage: 90, 
        icon: 'FaDatabase', 
        color: '#D97706',
        level: 'Expert',
        category: 'Backend & Databases',
        description: 'Designing transactional database models, executing complex relational joins, and managing state mutations.'
    },
    { 
        name: 'NumPy & Pandas', 
        percentage: 85, 
        icon: 'SiPandas', 
        color: '#150458',
        level: 'Advanced',
        category: 'Backend & Databases',
        description: 'Executing multidimensional vector operations, loading structured records dataframes, and extracting analytical insights.'
    },
    { 
        name: 'MongoDB', 
        percentage: 75, 
        icon: 'SiMongodb', 
        color: '#47A248',
        level: 'Advanced',
        category: 'Backend & Databases',
        description: 'Experience handling document-oriented schemas, CRUD operations, indexing, and aggregations.'
    },
    { 
        name: 'WebSockets & Async I/O', 
        percentage: 85, 
        icon: 'FaPlug', 
        color: '#F43F5E',
        level: 'Advanced',
        category: 'Backend & Databases',
        description: 'Engineered non-blocking, multi-channel concurrent state pipes for real-time game communications.'
    },

    // --- DEVOPS & TOOLS ---
    { 
        name: 'Docker & Containers', 
        percentage: 85, 
        icon: 'FaDocker', 
        color: '#2496ED',
        level: 'Advanced',
        category: 'DevOps & Tools',
        description: 'Packaging applications, setting multi-container docker-compose spaces, and local network alignments.'
    },
    { 
        name: 'Git & GitHub', 
        percentage: 90, 
        icon: 'FaGithub', 
        color: '#181717',
        level: 'Expert',
        category: 'DevOps & Tools',
        description: 'Executing clean branching strategies, resolving conflict issues, maintaining code bases, and automated CI/CD.'
    },
    { 
        name: 'Figma', 
        percentage: 80, 
        icon: 'FaFigma', 
        color: '#A259FF',
        level: 'Advanced',
        category: 'DevOps & Tools',
        description: 'Creating wireframe models, prototyping vector flows, and mapping visual user-journeys.'
    },
    { 
        name: 'Linux & Bash', 
        percentage: 85, 
        icon: 'FaLinux', 
        color: '#FCC624',
        level: 'Advanced',
        category: 'DevOps & Tools',
        description: 'Structuring automation scripting, handling directory permissions, and configuring virtual servers environments.'
    },

    // --- SOFT SKILLS ---
    { 
        name: 'Technical Teaching', 
        percentage: 95, 
        icon: 'FaChalkboardTeacher', 
        color: '#06B6D4',
        level: 'Expert',
        category: 'Soft Skill',
        description: 'Teaching Assistant for DBMS and Data Structures at FAST-NUCES, mentoring 80+ cohort students.'
    },
    { 
        name: 'Algorithmic Reasoning', 
        percentage: 95, 
        icon: 'FaLightbulb', 
        color: '#FFD700',
        level: 'Expert',
        category: 'Soft Skill',
        description: 'Strong analytical skills for designing mathematically sound, low-overhead algorithms and structures.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 220,
        damping: 18,
        duration: 0.4
      }
    }
  };

  const getProgressWidth = (percentage: number) => {
    return `${percentage}%`;
  };

  // Get unique categories
  const categories = ['all', 'AI & Agentic Systems', 'Language', 'Framework', 'Backend & Databases', 'DevOps & Tools', 'Soft Skill'];
  
  // Filter skills based on selected category
  const filteredSkills = activeCategory === 'all' 
    ? (limit && isMobile && !showAll ? skills.slice(0, limit) : skills)
    : skills.filter(skill => skill.category === activeCategory);
    
  return (
    <section id="skills" className="skills-section">
      <div className="radial-glow-skills" />
      <Container>
        <motion.div 
          ref={skillsHeaderRef}
          className="section-title-wrapper text-center mb-5"
          initial={{ opacity: 0, y: -20 }}
          animate={skillsHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          <div className="section-title">
            <h2>My Skillset</h2>
          </div>
          <motion.p
            className="section-subtitle mt-2 text-secondary"
            initial={{ opacity: 0 }}
            animate={skillsHeaderInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Technical proficiencies built at FAST-NUCES and implemented in production-ready systems.
          </motion.p>
        </motion.div>
        
        {/* Category Filter */}
        <motion.div
          className="category-filter text-center mb-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="filter-buttons-wrapper">
            {categories.map((category, index) => (
              <motion.div
                key={index}
                className="d-inline-block m-1"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Button
                  variant={activeCategory === category ? "primary" : "outline-primary"}
                  onClick={() => setActiveCategory(category)}
                  className={`category-btn px-4 py-2 ${activeCategory === category ? 'active' : ''}`}
                >
                  {category === 'all' ? 'All Skills' : category}
                </Button>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div 
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "visible"}
          className="skills-container"
        >
          <Row className="justify-content-center">
            <AnimatePresence mode="popLayout">
              {filteredSkills.map((skill, index) => (
                <Col lg={6} key={skill.name} className="mb-4">
                  <motion.div 
                    className="skill-item p-4"
                    variants={itemVariants}
                    layout
                    whileHover={{ 
                      y: -6, 
                      borderColor: 'var(--accent-color)',
                      boxShadow: '0 12px 30px rgba(99, 102, 241, 0.12)',
                    }}
                  >
                    <div className="skill-header d-flex align-items-center mb-3">
                      <motion.div 
                        className="skill-icon d-flex align-items-center justify-content-center me-3"
                        style={{ color: skill.color, background: `${skill.color}15` }}
                        whileHover={{ 
                          rotate: 15,
                          scale: 1.1,
                        }}
                      >
                        <Icon icon={skill.icon} className="icon-class" />
                      </motion.div>
                      <div className="skill-meta flex-grow-1">
                        <h4 className="skill-name mb-1">{skill.name}</h4>
                        <div className="skill-badges d-flex align-items-center gap-2">
                          <span className={`level-badge level-${skill.level.toLowerCase()} px-2 py-0.5 rounded`}>
                            {skill.level}
                          </span>
                          <span className="category-badge px-2 py-0.5 rounded text-muted">
                            {skill.category}
                          </span>
                        </div>
                      </div>
                      <div className="skill-percent font-weight-bold text-end">
                        <span className="percentage-text" style={{ color: skill.color }}>{skill.percentage}%</span>
                      </div>
                    </div>
                    
                    <div className="skill-description mb-3">
                      <p className="mb-0 text-secondary">{skill.description}</p>
                    </div>
                    
                    <div className="skill-progress-bg rounded-pill overflow-hidden">
                      <motion.div 
                        className="skill-progress h-100 rounded-pill"
                        style={{ backgroundColor: skill.color }}
                        initial={{ width: 0 }}
                        whileInView={{ 
                          width: getProgressWidth(skill.percentage) 
                        }}
                        transition={{ 
                          duration: 0.8, 
                          ease: "easeOut",
                        }}
                        viewport={{ once: true }}
                      />
                    </div>
                  </motion.div>
                </Col>
              ))}
            </AnimatePresence>
          </Row>
        </motion.div>

        {limit && isMobile && activeCategory === 'all' && (
          <div className="text-center mt-4">
            <motion.div
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="d-inline-block"
            >
              <Button 
                onClick={() => setShowAll(!showAll)} 
                className="category-btn px-5 py-2.5 active"
                style={{ minWidth: '200px' }}
              >
                {showAll ? 'Show Less' : 'Show All Skills'}
              </Button>
            </motion.div>
          </div>
        )}

      </Container>
    </section>
  );
};

export default Skills;
